import { useSelector } from "react-redux";

// Types
import { ILocationState } from "@/Common/interfaces/location";

// MUI
import { Skeleton, Stack, Typography } from "@mui/material";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// Components
import FnIDetailsTable from "./FnIDetailsTable";

// Hooks
import { useFniDetailsQuery } from "./hooks/useFniDetailsQuery";
import { useMappingFilters } from "@/Common/hooks/meta/useMappingFilters";
import useConfigurationData from "@/Common/hooks/Configuration/useConfigurationData";
import { useFniDetailsUpdateSubscription } from "./hooks/useFniDetailsUpdateSubscription";

interface FniDetailsLayerProps {
	periodTreeSelection: IPeriodTreeSelection;
	isTabSelected: boolean;
	hasPermission: boolean;
}

export default function FnIDetailsBody(props: Readonly<FniDetailsLayerProps>) {
	const { periodTreeSelection, isTabSelected, hasPermission } = props;

	const commissionCycleId = periodTreeSelection?.cycleId ?? "";
	const payPeriodId = periodTreeSelection?.payPeriodId ?? "";

	const locationId = useSelector((state: ILocationState) => state.location.location?.locationId);

	const { configuration, error: loadConfigError, isLoading: isLoadingConfiguration } = useConfigurationData({ locationId });
	const errorLoadingConfig = Boolean(loadConfigError);
	const dmsProvider = configuration?.administrationTools?.integrations?.dmsProvider?.provider ?? "";

	const {
		isLoadingFilters,
		mappingFiltersData,
		error: loadFiltersError,
	} = useMappingFilters({
		enabled: and(isTabSelected, Boolean(dmsProvider)),
		provider: dmsProvider,
		category: "F&ID",
	});
	const errorLoadingFilters = Boolean(loadFiltersError);

	const hasErrorLoadingPreconditions = errorLoadingConfig || errorLoadingFilters;

	const {
		fniDetailsData,
		isPaginating,
		isLoading: isLoadingFniDetails,
	} = useFniDetailsQuery({
		locationId,
		commissionCycleId,
		payPeriodId,
		enabled: and(isTabSelected, !hasErrorLoadingPreconditions, hasPermission),
	});

	useFniDetailsUpdateSubscription({
		locationId,
		commissionCycleId,
		payPeriodId,
	});

	const hasFniDetailsData = !!fniDetailsData?.length;
	const noDataLoaded = and(!isLoadingConfiguration, !isLoadingFilters, !isLoadingFniDetails, !hasFniDetailsData);
	const isLoading = or(isLoadingConfiguration, isLoadingFniDetails, isLoadingFilters);

	return (
		<Stack className="py-8" height={690}>
			<Header title={periodTreeSelection?.titleExtended} />

			{hasErrorLoadingPreconditions && <ErrorState />}

			{!hasErrorLoadingPreconditions && (
				<>
					{noDataLoaded && <EmptyState />}

					<Stack className="pt-4 !h-full">
						{isLoading ? (
							<TableLoader toolbar buttons={2} content rows={5} multiTitles={8} />
						) : (
							hasFniDetailsData && (
								<FnIDetailsTable
									isPaginating={isPaginating}
									items={fniDetailsData}
									mappingFilters={mappingFiltersData ?? []}
								/>
							)
						)}
					</Stack>
				</>
			)}
		</Stack>
	);
}

// MARK:- Supporting Components
function Header(props: { title?: string | null }) {
	const { title } = props;

	if (!title) {
		return <Skeleton variant="rectangular" height={30} width={350} className={"rounded-[6px] mb-[15px]"} />;
	}
	return <Typography className={"page-title"}>{`${title} F&I Details`}</Typography>;
}

function ErrorState() {
	return <EmptyBody image={empty} title={"There was an error while loading data"} bodyHeightClassName={"!h-[380px]"} />;
}

function EmptyState() {
	return <EmptyBody image={emptyTable} title={"No F&I Details Data for this period"} bodyHeightClassName={"!h-[380px]"} />;
}
